// LightingStage — the hillshade term (plan §8).  Ports the existing CPU bake formula out of
// TerrainWorker.bakeChunkBitmapWithCtx into a GLSL prelude + JS helpers.  Per D6 this is the
// SINGLE file the later PBR swap replaces — every material already outputs a full
// `MaterialSample` (albedo/normal/roughness/height) from day one, so PBR only touches this
// file's GLSL (consume roughness/height too) without touching any recipe.
//
// Set14 P8 — the D6 swap is DONE: the ROUGHNESS-AWARE path is now Cook-Torrance GGX PBR — the
// SAME direct-light BRDF three's `MeshStandardMaterial` uses (D_GGX + V_GGX_SmithCorrelated +
// Schlick F, dielectric F0 = 0.04, the UE4/Disney `alpha = roughness^2` reparam).
//
// Set14 P9 (2026-08-17) — the FULL terrain↔scenery shading unification, closing the gap P8
// deliberately left open.  P8 swapped only the SPECULAR lobe while preserving the old
// `blended = 1 - strength + strength * shade` envelope — which was a LAMBERT hillshade blend, not
// three's light model.  Consequences (the "strength does nothing to the bright areas" bug):
//   • `strength` was a 0..1 blend (how much hillshade to apply), NOT a light intensity — so at a
//     fully-lit normal `shade` pinned to 1 and NO value of `strength` could dim the lit peak.
//   • the diffuse had no `RECIPROCAL_PI` (three's BRDF_Lambert), so the absolute scale was ~3.14×
//     off from the bake's MeshStandardMaterial.
//   • the specular was multiplied by `albedo` (albedo-tinted highlights) and had NO `dotNL` /
//     light-strength factor — energy-wrong vs three (which multiplies by `irradiance = dotNL*S`).
//   • albedo was fed as linear but chosen in sRGB (the color-picker) — a transfer-curve mismatch.
//
// This file now implements three's `MeshStandardMaterial` direct-light model VERBATIM (metalness
// 0), so the terrain and the bake's trees sit on ONE shading model and the impostor + the future
// 3D near tier differ only in resolution (goal §1.2).  The `BakeLightRig` (Scenery) and the
// `TerrainHeightShadingConfig` share the SAME semantics:
//   • `light_azimuth` / `light_altitude` → light direction (`u_lightDir`)
//   • `ambient`         → AmbientLight.intensity (omnidirectional fill / shadow floor)
//   • `strength`        → DirectionalLight.intensity (the SUN — scales the lit peak AND highlight)
// `slope_scale` stays terrain-only (geometric-normal exaggeration; correctly excluded from the
// bake rig — it has no baked-geometry analogue).
//
// BRDF (three r0.174, mirroring meshphysical/RE_Direct_Physical, metalness 0):
//   irradiance        = dotNL * u_sunIntensity       (DirectionalLight)
//   ambientIrradiance = u_ambient                     (AmbientLight)
//   diffuse  = (irradiance + ambientIrradiance) * BRDF_Lambert(albedo)   [= ... * albedo / PI]
//   specular = irradiance * BRDF_GGX(D, V, F) * u_specStrength * specStrength
//   out      = diffuse + specular
// `u_slopeScale` is retained as a declared uniform (the renderer still sets it; harmless if
// inactive) but the shade function no longer reads it.
//
// Normal blending order (plan §8, Phase 4): the worker ALREADY applies `slope_scale` to the
// GEOMETRIC normal (the per-sample u_geoNormal lattice); the material's detail normal is
// whiteout/RNM-blended in AFTER that (never addition).  Under `slope_scale <= 0` the geometric
// normal collapses to straight-up and whiteout(up, detail) == detail, so the detail normal alone
// carries the relief — no gate needed.

/** GLSL: the light uniforms consumed by {@link LIGHTING_GLSL}.  The renderer sets these once per
 *  frame (they are camera/shading-config-fixed, not per-chunk). */
export const LIGHTING_UNIFORM_DECLS = `
uniform vec3  u_lightDir;       // light direction (world space, unit length) — from TerrainHeightShadingConfig.light_azimuth/altitude
uniform float u_ambient;        // AmbientLight.intensity — TerrainHeightShadingConfig.ambient (omnidirectional fill / shadow floor)
uniform float u_sunIntensity;   // DirectionalLight.intensity — TerrainHeightShadingConfig.strength (the SUN; scales the lit peak AND the highlight)
uniform float u_slopeScale;     // TerrainHeightShadingConfig.slope_scale (applies to the GEOMETRIC normal only — worker-side, in u_geoNormal)
uniform sampler2D u_geoNormal;  // per-chunk geometric-normal lattice (RGBA32F, z-up, slope_scale ALREADY applied worker-side) — per-SAMPLE, fetched at the fragment's lattice coord (granularity = fine-cell size, matching the old CPU bake; the Phase-3 centre-sample uniform collapsed hillshade to one slope per chunk)
uniform float u_specStrength;   // Phase 4 optional normal-only specular lobe (plan §8) — 0 disables; default 0.15
uniform float u_opacity;        // terrain overlay opacity (MapSettings_Terrain.overlay_opacity) — written to out_Color.a
uniform vec3  u_cameraPos;      // Set15 P3 (D10) — the camera's world position, for the REAL view vector in specular. Set per frame by the renderer (NOT three's built-in cameraPosition, which the raw compile path lacks).
// Set15 P8 (§6.2) — shadow mapping. The shadow-map UNIFORMS are declared here so a single
// assembled program carries them regardless of splat/region paths; the renderer sets them per
// chunk from the module-global shadow light (TerrainShadows.ts) — mirroring u_lightDir/u_cameraPos
// (NOT three's auto-uploaded light struct uniforms, which need material.lights = true and
// GLSL1 chunks). u_shadowEnabled gates the whole sample (0 in 2D, getShadowMask returns 1.0
// without touching the texture, keeping the 2D path byte-identical — D4). The shadow matrix is
// three's full texture-matrix (lightViewProj * bias), so the sample divides by .w.
uniform sampler2D u_shadowMap;      // the directional shadow depth texture (RGBA depth-packed)
uniform mat4      u_shadowMatrix;   // light's shadow.matrix (projection * view * bias → clip→uv)
uniform vec2      u_shadowMapSize;  // shadow-map resolution (px per side, for texel filtering)
uniform float     u_shadowBias;     // depth bias to fight acne (the light's shadow.bias)
uniform float     u_shadowEnabled;  // 1 in 3D with a shadow light, 0 otherwise (byte-identical 2D)
`;
/** GLSL prelude: the PBR lighting model (three's MeshStandardMaterial direct-light, metalness 0)
 *  + the after-`slope_scale` detail-normal blend.  Emitted by the assembler when lighting is
 *  enabled.  See the file header for how this maps onto the bake's light rig. */
export const LIGHTING_GLSL = `
// Whiteout normal blend (improved normal-map blending; the RNM/whiteout family the plan calls
// for): combine xy (detail tilts the geo) and multiply z (a detail normal never flattens the
// base below itself).  Never addition.
vec3 blendNormals(vec3 geoNormal, vec3 detailNormal) {
	return normalize(vec3(geoNormal.xy + detailNormal.xy, geoNormal.z * detailNormal.z));
}

// Re-tilt a light direction to a different ALTITUDE, keeping its AZIMUTH.  Used by the per-recipe
// lightAltitudeOverride (MaterialRecipe.lightAltitudeOverride): a level material viewed from
// straight down cannot show a tight specular lobe at a low sun, because H = normalize(L + +z)
// sits (90-alt)/2 degrees off vertical and the material's own micro-slopes never reach that tilt.
// Raising the altitude for THIS material only brings H within reach of its wave slopes.
vec3 tiltLightAltitude(vec3 L, float altDeg) {
	float hLen = length(L.xy);
	vec2 azDir = hLen > 1e-6 ? L.xy / hLen : vec2(0.0, 1.0);
	float a = radians(clamp(altDeg, 0.0, 90.0));
	return vec3(azDir * cos(a), sin(a));
}

// Cook-Torrance GGX specular (mirrors three's meshphysical BRDF_GGX, metalness 0) — the D8/P8
// swap.  The specular is scaled by irradiance (= dotNL * u_sunIntensity), exactly as three's
// RE_Direct_Physical multiplies BRDF_GGX by irradiance — so highlights scale with the sun AND
// vanish on unlit/back-facing slopes (unlike the old lobe, which was added unconditionally).
// Set15 P3 (D10) — V is now a parameter (the REAL view vector) instead of the hard-coded
// (0,0,1) top-down assumption: dotNV / dotVH / H = normalize(L + V) all use it, so the GGX
// lobe is correct under a perspective camera rather than coincidentally plausible.
float ggxSpecular(vec3 n, vec3 L, vec3 V, float roughness) {
	const float RECIPROCAL_PI = 0.3183098861837907;
	const float F0 = 0.04;   // dielectric specular colour (three's default specularColor 0xffffff → f0=0.04)
	const float EPSILON = 1e-6;
	// Disney reparam: alpha = roughness^2 (three: "alpha is roughness squared").
	float alpha = max(roughness, 0.02) * max(roughness, 0.02);
	float a2 = alpha * alpha;
	vec3 H = normalize(L + V);
	float dotNL = max(dot(n, L), 0.0);
	float dotNV = max(dot(n, V), 0.0);
	float dotNH = max(dot(n, H), 0.0);
	float dotVH = max(dot(V, H), 0.0);
	// D_GGX — GGX/Trowbridge-Reitz NDF (three: D_GGX(alpha, dotNH)).
	float denom = dotNH * dotNH * (a2 - 1.0) + 1.0;
	float D = RECIPROCAL_PI * a2 / (denom * denom);
	// V_GGX_SmithCorrelated — geometric shadowing + Smith visibility (three: V_GGX_SmithCorrelated).
	float gv = dotNL * sqrt(a2 + (1.0 - a2) * dotNV * dotNV);
	float gl = dotNV * sqrt(a2 + (1.0 - a2) * dotNL * dotNL);
	float vis = 0.5 / max(gv + gl, EPSILON);
	// F — Schlick fresnel (dielectric: F0 = 0.04, F90 = 1).
	float F = F0 + (1.0 - F0) * pow(max(1.0 - dotVH, 0.0), 5.0);
	return F * vis * D;
}

// Set15 P8 (§6.2) — shadow-mask sample. Reads the directional shadow depth texture via three's
// texture-matrix convention (shadowCoord.xyz /= .w → [0,1] uv + a depth compare), with a small
// 2×2 box for softening. Gated by u_shadowEnabled: when 0 (2D, or 3D without a shadow light)
// the sample returns 1.0 WITHOUT touching the texture — so the 2D path is byte-identical and a
// null/unset u_shadowMap can never be sampled. unpackRGBAToDepth is three's standard depth
// decode, defined locally because the assembled fragment compiles standalone (no <packing>).
float unpackRGBAToDepth(vec4 v) {
	vec4 bits = vec4(1.0, 1.0 / 255.0, 1.0 / 65025.0, 1.0 / 16581375.0);
	return dot(v, bits);
}
float getShadowMask() {
	if (u_shadowEnabled < 0.5) return 1.0;
	vec4 coord = u_shadowMatrix * vec4(vWorldPos, 1.0);
	coord.xyz /= coord.w;
	if (coord.x < 0.0 || coord.x > 1.0 || coord.y < 0.0 || coord.y > 1.0 || coord.z < 0.0 || coord.z > 1.0) return 1.0;
	coord.z += u_shadowBias;
	vec2 texelSize = 1.0 / u_shadowMapSize;
	// 2×2 box PCF (a cheap soft edge without a big kernel).  NOTE: GLSL ES has NO nested
	// function declarations, so the depth compare is inlined (no local compareDepth fn).
	float d = unpackRGBAToDepth(texture(u_shadowMap, coord.xy));
	d += unpackRGBAToDepth(texture(u_shadowMap, coord.xy + vec2(texelSize.x, 0.0)));
	d += unpackRGBAToDepth(texture(u_shadowMap, coord.xy + vec2(0.0, texelSize.y)));
	d += unpackRGBAToDepth(texture(u_shadowMap, coord.xy + texelSize));
	d *= 0.25;
	return step(coord.z, d);
}

// The THREE-light model (metalness 0).  NEGATIVE lightAltOverrideDeg = use the global u_lightDir
// as-is; otherwise re-tilt for THIS material (the per-recipe lightAltitudeOverride).  Azimuth,
// ambient, strength/sun-intensity and slope_scale all come from the global rig.
//  - albedo        = MaterialSample.albedo (LINEAR)
//  - geoNormal     = worker lattice (slope_scale applied) — straight-up under flatGeoNormal
//  - detailNormal  = the material's own (world-XZ) normal
//  - roughness     = MaterialSample.roughness
//  - specStrength  = per-recipe specular opt-out multiplier (0 = pure hillshade, no lobe)
vec3 lightingShade(vec3 albedo, vec3 geoNormal, vec3 detailNormal, float roughness, float specStrength, float lightAltOverrideDeg) {
	vec3 L = lightAltOverrideDeg >= 0.0 ? tiltLightAltitude(u_lightDir, lightAltOverrideDeg) : u_lightDir;
	vec3 n = blendNormals(geoNormal, detailNormal);
	float dotNL = max(dot(n, L), 0.0);
	// Set15 P3 (D10) — the REAL view vector (fragment → camera), gated by u_displaceScale so
	// the 2D top-down path stays byte-identical with the old hard-coded (0,0,1) (the 2D ortho
	// camera looks straight down, where the true V IS ~(0,0,1) for every point; 3D needs the
	// perspective-correct vector or the GGX lobe is wrong).  vWorldPos comes from the displaced
	// vertex (interpolated); u_cameraPos is the camera's world position (set per frame).
	vec3 V = mix(vec3(0.0, 0.0, 1.0), normalize(u_cameraPos - vWorldPos), u_displaceScale);
	// Directional + ambient irradiance — three's RE_Direct_Physical / getAmbientLightIrradiance.
	// Set15 P8 (§6.2) — the DIRECT term is multiplied by the shadow mask (never ambient): the
// directional light casts; the ambient fill is omnidirectional and unshadowed. getShadowMask
// is 1.0 when u_shadowEnabled is 0 (2D / no shadow light), so this is a byte-identical no-op
	// on the 2D path (D4).
	float shadowMask = getShadowMask();
	float irradiance        = dotNL * u_sunIntensity * shadowMask;   // DirectionalLight.intensity = strength
	float ambientIrradiance = u_ambient;                             // AmbientLight.intensity
	// BRDF_Lambert (three: diffuseColor / PI) + BRDF_GGX scaled by irradiance.
	vec3 diffuse  = (irradiance + ambientIrradiance) * (albedo * 0.3183098861837907);
	vec3 specular = vec3(irradiance * ggxSpecular(n, L, V, roughness)) * u_specStrength * specStrength;
	return diffuse + specular;
}

// 5-arg form — the common case (no per-recipe lightAltitudeOverride): forwards to the 6-arg
// form with NEGATIVE override = use the global u_lightDir as-is.
vec3 lightingShade(vec3 albedo, vec3 geoNormal, vec3 detailNormal, float roughness, float specStrength) {
	return lightingShade(albedo, geoNormal, detailNormal, roughness, specStrength, -1.0);
}`;
export interface HypsometricStopLike { elevation: number; color: [number, number, number]; }

/** JS port of TerrainWorker's `computeLightDir` — the renderer computes the light direction once
 *  per shading-config change and writes it into `u_lightDir`.  Mirrors the worker so both paths
 *  (old bake + new shader) agree on the light rig. */
export function computeLightDirJS(azimuthDeg: number, altitudeDeg: number): [number, number, number] {
	const azRad = azimuthDeg * Math.PI / 180;
	const altRad = altitudeDeg * Math.PI / 180;
	return [
		Math.cos(altRad) * Math.sin(azRad),
		Math.cos(altRad) * Math.cos(azRad),
		Math.sin(altRad),
	];
}

/** JS port of the worker's `sampleStop` — resolves the hypsometric-ramp colour for an elevation
 *  (returns a 0..255 RGB triple, byte-equivalent to the bake so the shader path looks identical
 *  to the old path before material layers are applied).  Used by the renderer to compute each
 *  chunk's `u_baseAlbedo` from its per-sample heights (the ramp is DEMOTED to base albedo input,
 *  plan §8).  Falls back to mid-grey when no stops are configured (parity with the bake). */
export function sampleHypsometricRampJS(elevation: number, stops: HypsometricStopLike[]): [number, number, number] {
	if (stops.length === 0) return [128, 128, 128];
	if (elevation <= stops[0].elevation) return stops[0].color;
	const last = stops[stops.length - 1];
	if (elevation >= last.elevation) return last.color;
	for (let i = 0; i < stops.length - 1; i++) {
		if (elevation <= stops[i + 1].elevation) {
			const a = stops[i].color, b = stops[i + 1].color;
			const t = (elevation - stops[i].elevation) / (stops[i + 1].elevation - stops[i].elevation);
			return [
				Math.round(a[0] + (b[0] - a[0]) * t),
				Math.round(a[1] + (b[1] - a[1]) * t),
				Math.round(a[2] + (b[2] - a[2]) * t),
			];
		}
	}
	return last.color;
}
